/**
 * Login Page Component
 * Authentication page with login form
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button, Input, Message } from '../components/ui';
import safeInternalRedirect from '../utils/safeRedirect';
import './Login.css';

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = safeInternalRedirect(searchParams.get('redirect'));

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: false,
  });
  const [message, setMessage] = useState({ text: '', type: '' });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
          rememberMe: formData.rememberMe,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Store token
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('tokenExpiry', data.expires.toString());
        localStorage.setItem('currentUser', data.username);

        showMessage('Login successful!', 'success');

        // Call parent callback if provided
        if (onLogin) {
          onLogin(data);
        }

        // Redirect after short delay
        setTimeout(() => {
          navigate(redirectUrl);
        }, 500);
      } else {
        // Warn before the server locks the account out after 5 failed attempts
        const attemptsNote =
          typeof data.attemptsRemaining === 'number' && data.attemptsRemaining > 0
            ? ` ${data.attemptsRemaining} attempt(s) remaining before login is temporarily locked.`
            : '';
        showMessage((data.error || 'Login failed') + attemptsNote, 'error');
      }
    } catch (error) {
      showMessage('Network error: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="rf-login-page">
      <div className="rf-center-container">
        <div className="rf-auth-card">
          <div className="rf-auth-logo">
            <i className="fas fa-lock"></i>
            <span>Rapid Fusion Portal</span>
          </div>

          <form onSubmit={handleSubmit} className="rf-login-form">
            <Input
              label="Username or Email"
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              autoComplete="username"
              placeholder="Enter your username or email"
            />

            <Input
              label="Password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              autoComplete="current-password"
              placeholder="Enter your password"
            />

            <div className="rf-remember-me">
              <label className="rf-checkbox-label">
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  className="rf-checkbox"
                />
                <span className="rf-checkbox-text">Remember me for 30 days</span>
              </label>
            </div>

            <Button
              type="submit"
              variant="primary"
              className="rf-key-action"
              block
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </Button>
          </form>

          {message.text && (
            <Message type={message.type}>{message.text}</Message>
          )}

          <div className="rf-login-footer">
            <p>Don't have an account?</p>
            <Link to="/request-access" className="rf-request-access-link">
              <i className="fas fa-user-plus"></i>
              Request Access
            </Link>
            <p className="rf-login-footer-secondary">Already submitted a request?</p>
            <Link to="/check-status" className="rf-check-status-link">
              <i className="fas fa-search"></i>
              Check Request Status
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
